import { MessageSquareText, Mail } from "lucide-react";
import { SmsMeter } from "./SmsMeter";
import { V } from "@/lib/vocab";

const inputCls = "w-full rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30";

const SmsPreview = ({ text }) => !text ? null : (
  <div data-testid="sms-preview" className="mt-2 flex justify-end">
    <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-ink text-white px-3.5 py-2 text-[13px] leading-snug whitespace-pre-wrap shadow-sm">{text}</div>
  </div>
);

// The words. One box when text and email say the same thing; two when they don't.
export const CopyEditor = ({ channels, sameCopy, setSameCopy, title, setTitle, message, setMessage, smsBody, setSmsBody,
  meta, onNormalize, normalizing, personal, urgent, toolbar, smsPreview }) => {
  const sms = channels.includes("sms"), email = channels.includes("email");
  const both = sms && email;
  const split = both && !sameCopy;
  const subjectLabel = email ? "Subject" : "Title";
  const subjectHint = personal ? (email ? "(optional — email only)" : "") : both && sameCopy ? "(optional — email subject and the text's first line)" : "(optional)";

  const subject = (
    <>
      <label className="block text-xs font-bold text-ink/50 mb-1">{subjectLabel} <span className="font-normal text-ink/40">{subjectHint}</span></label>
      <input data-testid="broadcast-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={90}
        placeholder={urgent ? "e.g. Water shut-off tonight 9pm" : personal ? "e.g. Your fob is ready" : "e.g. Pool reopening this weekend"}
        className={`${inputCls} mb-3`} />
    </>
  );

  return (
    <div data-testid="copy-editor">
      {both && (
        <label className="mb-3 inline-flex items-center gap-2 text-xs font-semibold text-ink/70 cursor-pointer select-none">
          <input type="checkbox" data-testid="copy-same-toggle" checked={sameCopy} onChange={(e) => setSameCopy(e.target.checked)} className="h-4 w-4 accent-ink" />
          Same words for the text and the email
        </label>
      )}

      {!split && (
        <>
          {(email || !personal) && subject}
          <div className="flex items-center justify-between mb-1">
            <label className="block text-xs font-bold text-ink/50">{sms && !email ? "Text" : email && !sms ? "Email" : "Message"}</label>
            {toolbar}
          </div>
          <textarea data-testid="broadcast-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={4} maxLength={sms ? 1000 : 4000}
            placeholder={personal ? "Hi — just a quick note from the office…" : `Write your message to ${V.lcs("person")}…`}
            className={`${inputCls} resize-y`} />
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">{sms && <SmsMeter meta={meta} onNormalize={() => onNormalize("message")} normalizing={normalizing} />}</div>
            <div className="text-[11px] text-ink/40 mt-1 shrink-0">{message.length}/{sms ? 1000 : 4000}</div>
          </div>
          {sms && <SmsPreview text={smsPreview} />}
        </>
      )}

      {split && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-2xl border border-sand-200 p-3.5">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5 text-xs font-bold text-ink/60"><MessageSquareText className="h-3.5 w-3.5" /> Text</div>
              {toolbar}
            </div>
            <textarea data-testid="broadcast-sms-body" value={smsBody} onChange={(e) => setSmsBody(e.target.value)} rows={4} maxLength={1000}
              placeholder="Short and plain — this is the SMS" className={`${inputCls} resize-y`} />
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1"><SmsMeter meta={meta} onNormalize={() => onNormalize("sms")} normalizing={normalizing} /></div>
              <div className="text-[11px] text-ink/40 mt-1 shrink-0">{smsBody.length}/1000</div>
            </div>
            <SmsPreview text={smsPreview} />
          </div>
          <div className="rounded-2xl border border-sand-200 p-3.5">
            <div className="flex items-center gap-1.5 text-xs font-bold text-ink/60 mb-1.5"><Mail className="h-3.5 w-3.5" /> Email</div>
            {subject}
            <textarea data-testid="broadcast-email-body" value={message} onChange={(e) => setMessage(e.target.value)} rows={6} maxLength={4000}
              placeholder="The longer version — details, links, who to contact" className={`${inputCls} resize-y`} />
            <div className="text-[11px] text-ink/40 text-right mt-1">{message.length}/4000</div>
          </div>
        </div>
      )}
    </div>
  );
};
